import * as React from "react";
import { useEffect, useMemo, useState } from "react";
import { QueryClientProvider } from "@tanstack/react-query";
import useConfig from "@/hooks/useConfig";
import { TooltipProvider } from "@/v2/components/ui/tooltip";
import { Toaster } from "@/v2/components/ui/sonner";
import { getTenantThemeCss } from "@/v2/lib/theme-bridge";
import { queryClient } from "@/v2/lib/queryClient";
import "@/v2/styles/v2.css";

/**
 * Tracks whether a V2Shell is already mounted above this one. V2Shell is
 * documented as nestable (header shell inside page shell), but chrome like
 * the theme <style> tag and the Toaster should only ever render once:
 * sonner's toast queue is a module-level store, so two mounted Toasters
 * would each render every toast.
 */
const V2ShellNestingContext = React.createContext(false);

/**
 * The context gate above only catches ancestor/descendant nesting. The
 * rollout design also mounts V2Shell as siblings (a v1 header shell next to
 * a page shell), which share no context, so a second module-level counter
 * dedupes the Toaster across the whole document: every non-nested shell
 * registers on mount and releases on cleanup, and only the instance that
 * registers while the counter is at zero renders the Toaster. The
 * useState+useEffect split (rather than claiming during render) keeps this
 * StrictMode-safe: React's mount-cleanup-remount dance releases and
 * re-claims the count correctly instead of leaking a stale claim.
 */
let mountedTopLevelShellCount = 0;

function useOwnsToaster(isNested: boolean): boolean {
  const [ownsToaster, setOwnsToaster] = useState(false);

  useEffect(() => {
    if (isNested) return;
    const isFirstToRegister = mountedTopLevelShellCount === 0;
    mountedTopLevelShellCount += 1;
    setOwnsToaster(isFirstToRegister);
    return () => {
      mountedTopLevelShellCount -= 1;
    };
  }, [isNested]);

  return ownsToaster;
}

/**
 * Wraps any v2 surface (a full page or just the header) with the .v2 style
 * scope, tenant theming, and the providers v2 components assume. Nestable
 * and layout-neutral.
 */
export function V2Shell({ children }: { children: React.ReactNode }) {
  const isNested = React.useContext(V2ShellNestingContext);
  const ownsToaster = useOwnsToaster(isNested);
  const config = useConfig();
  const themeCss = useMemo(
    () => getTenantThemeCss({ primary: config?.primary, secondary: config?.secondary }),
    [config?.primary, config?.secondary]
  );

  return (
    <div className="v2">
      {!isNested && themeCss ? <style>{themeCss}</style> : null}
      <QueryClientProvider client={queryClient}>
        <TooltipProvider delayDuration={200}>
          <V2ShellNestingContext.Provider value={true}>{children}</V2ShellNestingContext.Provider>
        </TooltipProvider>
        {ownsToaster ? <Toaster /> : null}
      </QueryClientProvider>
    </div>
  );
}
